/**
 * The store's `EventSink` (REALTIME.md §3): applies live events to the cache and the reactive
 * state, and wires `LiveClient` + `LeaderElector` with `lastEventId` persisted in `sync["*"]`.
 */
import { db, purgeFolder, rowKey, SYNC_STREAM_KEY } from '$lib/db';
import { SNOOZED_FOLDER } from '$lib/mail/snooze';
import { LeaderElector } from '$lib/realtime/leader';
import { LiveClient, type EventSink, type LiveEvent, type LiveClientOptions } from '$lib/realtime/sse';
import type { MessageRow } from '$lib/types';
import { plainFolder } from './list.svelte';
import type { MailStore } from './mail.svelte';

export async function loadLastEventId(): Promise<number | null> {
  const rec = await db.sync.get(SYNC_STREAM_KEY);
  const n = Number(rec?.lastEventId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export async function saveLastEventId(id: number): Promise<void> {
  const prev = (await db.sync.get(SYNC_STREAM_KEY)) ?? { folder: SYNC_STREAM_KEY };
  await db.sync.put({ ...prev, folder: SYNC_STREAM_KEY, lastEventId: String(id) });
}

export class LiveModule implements EventSink {
  client: LiveClient | null = null;
  constructor(private readonly s: MailStore) {}

  /** Create (or recreate) the client. `overrides` lets tests inject fakes. */
  connect(overrides: Partial<LiveClientOptions> = {}): LiveClient {
    this.disconnect();
    const client = new LiveClient({
      sink: this,
      loadLastEventId,
      saveLastEventId,
      elector: overrides.elector === undefined ? new LeaderElector() : overrides.elector,
      ...overrides,
    });
    client.onState((st) => {
      this.s.connection = st;
    });
    this.client = client;
    void client.start();
    return client;
  }

  disconnect(): void {
    this.client?.stop();
    this.client = null;
  }

  currentFolder(): string {
    return this.s.currentFolder;
  }

  async resync(folders: string[]): Promise<void> {
    for (const f of new Set(folders)) await this.s.syncFolder(f).catch(() => undefined);
    this.s.scheduleFolderCount();
  }

  async apply(ev: LiveEvent): Promise<void> {
    switch (ev.type) {
      case 'hello':
        this.s.liveDown = false;
        this.s.watcherState = 'idle';
        return;
      case 'message.new':
        return this.onMessageNew(ev);
      case 'message.flags':
        return this.onFlags(ev.mailbox, ev.uid, ev.flags);
      case 'message.expunged':
        return this.onExpunged(ev.mailbox, ev.uids);
      case 'mailbox.status':
        return this.onStatus(ev.mailbox, ev.exists, ev.unseen);
      case 'mailbox.resync':
        await this.s.list.wipeFolder(ev.mailbox);
        await this.s.syncFolder(ev.mailbox, { full: true }).catch(() => undefined);
        this.s.scheduleFolderCount();
        return;
      case 'folders.changed':
        await this.s.refreshFolders().catch(() => undefined);
        return;
      case 'outbox.state':
        this.s.prefs.applyOutboxState(ev);
        return;
      case 'snooze.woke': {
        await this.s.syncFolder('INBOX').catch(() => undefined);
        const snoozed = this.s.snoozePath ?? (this.s.folders.some((f) => f.path === SNOOZED_FOLDER) ? SNOOZED_FOLDER : null);
        if (snoozed) await this.s.syncFolder(snoozed).catch(() => undefined);
        this.s.scheduleFolderCount();
        return;
      }
      case 'watcher.state':
        this.s.watcherState = ev.state;
        this.s.liveDown = ev.state !== 'idle';
        if (ev.state === 'auth_error') void this.s.checkSession();
        return;
      default:
        return;
    }
  }

  /** Insert the summary row (idempotent), toast, page notification, then reconcile. */
  private async onMessageNew(ev: Extract<LiveEvent, { type: 'message.new' }>): Promise<void> {
    const folder = ev.mailbox;
    const key = rowKey(folder, ev.uid);
    const existing = await db.messages.get(key);
    if (!existing) {
      const m = ev.message;
      const from = Array.isArray(m.from) ? m.from[0] : m.from;
      const row: MessageRow = {
        key,
        folder,
        uid: ev.uid,
        seq: m.seq ?? 0,
        subject: m.subject ?? '',
        date: m.date,
        fromName: from?.name || '',
        fromAddr: from?.address || '',
        flags: m.flags ?? [],
        size: m.size ?? 0,
        hasAttachments: !!m.hasAttachments,
        atts: [],
        list: m.list === true,
        toAddrs: m.toAddrs ?? [],
        receivedAt: m.receivedAt ?? undefined,
        messageId: m.messageId ?? null,
        inReplyTo: m.inReplyTo ?? null,
        references: m.references ?? [],
      };
      if (m.preview) row.snippet = m.preview;
      await db.messages.put(row);
      const f = this.s.folders.find((x) => x.path === folder);
      if (f) {
        f.messages = (f.messages ?? 0) + 1;
        if (!row.flags.includes('\\Seen')) f.unseen = (f.unseen ?? 0) + 1;
        this.s.folders = [...this.s.folders];
        void db.folders.put(plainFolder(f)).catch(() => undefined);
      }
      if (folder === this.s.currentFolder) await this.s.loadListFromCache(folder);
    }
    if (folder.toUpperCase() === 'INBOX') this.s.showToast('newMail');
    await this.s.syncFolder(folder).catch(() => undefined);
    if (folder.toUpperCase() === 'INBOX') this.s.notifyNew(this.s.msgs);
    this.s.scheduleFolderCount();
  }

  private async onFlags(folder: string, uid: number, flags: string[]): Promise<void> {
    const key = rowKey(folder, uid);
    const row = await db.messages.get(key);
    if (!row) return;
    const wasSeen = row.flags.includes('\\Seen');
    const nowSeen = flags.includes('\\Seen');
    await db.messages.put({ ...row, flags });
    const f = this.s.folders.find((x) => x.path === folder);
    if (f && wasSeen !== nowSeen) {
      f.unseen = Math.max(0, (f.unseen ?? 0) + (nowSeen ? -1 : 1));
      this.s.folders = [...this.s.folders];
      void db.folders.put(plainFolder(f)).catch(() => undefined);
    }
    if (folder === this.s.currentFolder) this.s.msgs = this.s.msgs.map((m) => (m.key === key ? { ...m, flags } : m));
  }

  private async onExpunged(folder: string, uids: number[]): Promise<void> {
    const keys = uids.map((u) => rowKey(folder, u));
    const rows = (await db.messages.bulkGet(keys)).filter((r): r is MessageRow => !!r);
    if (!rows.length) return;
    await db.messages.bulkDelete(keys);
    await db.bodies.bulkDelete(keys);
    const f = this.s.folders.find((x) => x.path === folder);
    if (f) {
      f.messages = Math.max(0, (f.messages ?? 0) - rows.length);
      f.unseen = Math.max(0, (f.unseen ?? 0) - rows.filter((r) => !r.flags.includes('\\Seen')).length);
      this.s.folders = [...this.s.folders];
      void db.folders.put(plainFolder(f)).catch(() => undefined);
    }
    if (folder === this.s.currentFolder) {
      const gone = new Set(keys);
      this.s.msgs = this.s.msgs.filter((m) => !gone.has(m.key));
      if (this.s.searchResults) this.s.searchResults = this.s.searchResults.filter((m) => !gone.has(m.key));
      this.s.folderLoaded = Math.max(0, this.s.folderLoaded - rows.length);
    }
  }

  private async onStatus(folder: string, exists: number, unseen: number): Promise<void> {
    const f = this.s.folders.find((x) => x.path === folder);
    if (!f) return;
    if (f.messages === exists && f.unseen === unseen) return;
    f.messages = exists;
    f.unseen = unseen;
    this.s.folders = [...this.s.folders];
    await db.folders.put(plainFolder(f)).catch(() => undefined);
    if (folder === this.s.currentFolder) this.s.folderTotal = exists;
  }

  /** Legacy `mailbox.resync` helper used by tests: wipe rows/bodies of a folder. */
  async wipe(folder: string): Promise<void> {
    await purgeFolder(folder);
  }
}
